import React, { Children } from 'react';
import { useParams } from 'react-router-dom';
import { HeartIcon, GlobeIcon, MapPinIcon, ExternalLinkIcon, TrendingUpIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import BackButton from '../../components/UI/BackButton';
// Mock data
const nonprofits = {
  'childrens-literacy-foundation': {
    name: "Children's Literacy Foundation",
    slug: 'childrens-literacy-foundation',
    theme: 'Early Childhood Education',
    location: 'National',
    website: 'https://example.org',
    description: 'Dedicated to inspiring a love of reading and writing among low-income, at-risk, and rural children. We provide new books, literacy programs, and library support to communities in need.',
    totalRaised: 12500,
    donorCount: 245,
    breakdown: {
      authors: 45,
      readers: 30,
      vendors: 15,
      platform: 10
    }
  },
  'book-access-for-all': {
    name: 'Book Access for All',
    slug: 'book-access-for-all',
    theme: 'Educational Equity',
    location: 'International',
    website: 'https://example.org',
    description: 'Ensuring every child has access to books regardless of their socioeconomic status. We build libraries, distribute books, and support local authors in developing nations.',
    totalRaised: 9800,
    donorCount: 180,
    breakdown: {
      authors: 50,
      readers: 25,
      vendors: 20,
      platform: 5
    }
  }
};
const recentDonations = [{
  amount: 250,
  donor: 'Sarah J. Maas',
  type: 'Author',
  date: '2023-12-05'
}, {
  amount: 50,
  donor: 'Reader123',
  type: 'Reader',
  date: '2023-12-04'
}, {
  amount: 100,
  donor: 'Bookish Merch Co',
  type: 'Vendor',
  date: '2023-12-03'
}, {
  amount: 500,
  donor: 'LitLaunch Match',
  type: 'Platform',
  date: '2023-12-01'
}, {
  amount: 75,
  donor: 'Fantasy Fanatic',
  type: 'Reader',
  date: '2023-11-28'
}];
const NonprofitProfile = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  // Fallback for demo purposes if slug doesn't match
  const nonprofit = nonprofits[slug as keyof typeof nonprofits] || nonprofits['childrens-literacy-foundation'];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-9442" data-mp="x3gxyg46w2e7">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9443" data-mp="1tuj7jbbdmjqf">
        <BackButton to="/donations" className="mb-6" data-id="element-9444" data-mp="1pu8rt6xs8eyd" />

        {/* Header Profile */}
        <div className={`${dk.card} rounded-xl overflow-hidden border shadow-sm mb-8`} data-id="element-9445" data-mp="1kuw28efj0ts3">
          <div className="h-32 bg-gradient-to-r from-indigo-500 to-purple-600" data-id="element-9446" data-mp="1fw1m5k8y1jbo"></div>
          <div className="px-8 pb-8 relative" data-id="element-9447" data-mp="1fy5rm6mhqqe2">
            <div className={`-mt-12 mb-4 inline-flex items-center justify-center h-24 w-24 rounded-xl shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`} data-id="element-9448" data-mp="9gr0ls2scvzx">
              <HeartIcon className="h-10 w-10 text-rose-500" data-id="element-9449" data-mp="3qiv55zkuvok6" />
            </div>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6" data-id="element-9450" data-mp="3vgwyddbzx49d">
              <div data-id="element-9451" data-mp="4ac13bvcl03w">
                <h1 className={`text-3xl font-bold mb-2 ${dk.textPrimary}`} data-id="element-9452" data-mp="49u7kn02kzzo">
                  {nonprofit.name}
                </h1>
                <div className="flex flex-wrap gap-4 text-sm" data-id="element-9453" data-mp="92wsy7up134s">
                  <span className={`flex items-center ${dk.textSecondary}`} data-id="element-9454" data-mp="d25lj9dzyuyr">
                    <GlobeIcon className="h-4 w-4 mr-1.5 opacity-70" data-id="element-9455" data-mp="i1qwyjacsvsd" />
                    {nonprofit.theme}
                  </span>
                  <span className={`flex items-center ${dk.textSecondary}`} data-id="element-9456" data-mp="n2oc0n02h9pn">
                    <MapPinIcon className="h-4 w-4 mr-1.5 opacity-70" data-id="element-9457" data-mp="s0pvh91enb1n" />
                    {nonprofit.location}
                  </span>
                  <a href={nonprofit.website} target="_blank" rel="noreferrer" className="flex items-center text-indigo-500 hover:text-indigo-600 font-medium" data-id="element-9458" data-mp="2oukn5vibohjw">
                    Visit Website
                    <ExternalLinkIcon className="h-3.5 w-3.5 ml-1" data-id="element-9459" data-mp="j3xpwfenzavk" />
                  </a>
                </div>
              </div>
              <div className="flex gap-3" data-id="element-9460" data-mp="x1uqro05i3g1">
                <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors" data-id="element-9461" data-mp="s67177hrb8a0">
                  Donate Now
                </button>
                <button className={`px-4 py-2 border font-medium rounded-lg transition-colors ${dk.border} ${dk.textPrimary} hover:${dk.surfaceMuted}`} data-id="element-9462" data-mp="er5mny82b7n">
                  Follow
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-9463" data-mp="3hto4u5nt602o">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8" data-id="element-9464" data-mp="3cw4sxcel1zdw">
            {/* About */}
            <div className={`${dk.card} rounded-xl p-6 border shadow-sm`} data-id="element-9465" data-mp="1sdkizu07xy5g">
              <h2 className={`text-xl font-bold mb-4 ${dk.textPrimary}`} data-id="element-9466" data-mp="1sb7usz3varjy">
                About
              </h2>
              <p className={`leading-relaxed ${dk.textSecondary}`} data-id="element-9467" data-mp="1jcmelalvbomc">
                {nonprofit.description}
              </p>
            </div>

            {/* Recent Donations */}
            <div className={`${dk.card} rounded-xl p-6 border shadow-sm`} data-id="element-9468" data-mp="1o9mltougexf1">
              <h2 className={`text-xl font-bold mb-6 ${dk.textPrimary}`} data-id="element-9469" data-mp="2c1tbk55oc65h">
                Recent Donations
              </h2>
              <div className="overflow-x-auto" data-id="element-9470" data-mp="2g0kamhtp9xv8">
                <table className="min-w-full" data-id="element-9471" data-mp="2222epwtn5f28">
                  <thead data-id="element-9472" data-mp="225883o9az33r">
                    <tr className={`border-b ${dk.divider}`} data-id="element-9473" data-mp="271yko1lwfgkf">
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-9474" data-mp="274bat21t8x8k">
                        Donor
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-9475" data-mp="bkp517n7mhbo">
                        Type
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-9476" data-mp="2rtzsdxqsllw">
                        Date
                      </th>
                      <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${dk.textMuted}`} data-id="element-9477" data-mp="3p1envvm8v6w1">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${dk.divider}`} data-id="element-9478" data-mp="3b13vup977m9a">
                    {recentDonations.map((donation, index) => <tr key={index} className={`text-sm ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} transition-colors`} data-id="element-9479" data-mp="1e70pc5cll0d7" data-mpi={index}>
                        <td className={`px-4 py-3 font-medium ${dk.textPrimary}`} data-id="element-9480" data-mp="1gq44cang8ygh">
                          {donation.donor}
                        </td>
                        <td className={`px-4 py-3 ${dk.textSecondary}`} data-id="element-9481" data-mp="1qkdy9ws1i4tb">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${donation.type === 'Author' ? 'bg-blue-100 text-blue-800' : donation.type === 'Reader' ? 'bg-emerald-100 text-emerald-800' : donation.type === 'Vendor' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`} data-id="element-9482" data-mp="1vgl1hht1x27k">
                            {donation.type}
                          </span>
                        </td>
                        <td className={`px-4 py-3 ${dk.textMuted}`} data-id="element-9483" data-mp="2j5xeksri1uf5">
                          {new Date(donation.date).toLocaleDateString()}
                        </td>
                        <td className={`px-4 py-3 text-right font-semibold ${dk.textPrimary}`} data-id="element-9484" data-mp="399196p6nzryw">
                          ${donation.amount}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Sidebar Stats */}
          <div className="space-y-6" data-id="element-9485" data-mp="3rzizyo7xsdhm">
            <div className={`${dk.card} rounded-xl p-6 border shadow-sm`} data-id="element-9486" data-mp="3ryhbni1wtuhy">
              <h2 className={`text-lg font-bold mb-6 ${dk.textPrimary}`} data-id="element-9487" data-mp="3n07ifczqx3yz">
                Impact Overview
              </h2>

              <div className="mb-8 text-center" data-id="element-9488" data-mp="5scafpdbpmjs">
                <div className={`text-4xl font-bold mb-1 ${dk.textPrimary}`} data-id="element-9489" data-mp="thmk90i17st">
                  ${nonprofit.totalRaised.toLocaleString()}
                </div>
                <div className={`text-sm ${dk.textMuted}`} data-id="element-9490" data-mp="27ml53fgzjct7">
                  Total Raised on LitLaunch
                </div>
              </div>

              <div className="space-y-4" data-id="element-9491" data-mp="i33cljdp8fiq">
                <div className="flex items-center justify-between" data-id="element-9492" data-mp="i44w8c8s6ac2">
                  <div className="flex items-center" data-id="element-9493" data-mp="n1enzyjn6y32">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mr-2" data-id="element-9494" data-mp="8djqm4uow5jn"></div>
                    <span className={`text-sm ${dk.textSecondary}`} data-id="element-9495" data-mp="8bfptvs37mnl">
                      Authors
                    </span>
                  </div>
                  <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-9496" data-mp="3vhzw7d4g9n54">
                    {nonprofit.breakdown.authors}%
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-9497" data-mp="3lkbq5r1e8t7n">
                  <div className="flex items-center" data-id="element-9498" data-mp="3lm6e7e7yp1fy">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 mr-2" data-id="element-9499" data-mp="3qidccku498nc"></div>
                    <span className={`text-sm ${dk.textSecondary}`} data-id="element-9500" data-mp="1js74gxahms5d">
                      Readers
                    </span>
                  </div>
                  <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-9501" data-mp="1or0hyppryj5j">
                    {nonprofit.breakdown.readers}%
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-9502" data-mp="1hc538yfulvfp">
                  <div className="flex items-center" data-id="element-9503" data-mp="13jyn3ccp3og9">
                    <div className="w-2 h-2 rounded-full bg-amber-500 mr-2" data-id="element-9504" data-mp="13ljjl7iklhgr"></div>
                    <span className={`text-sm ${dk.textSecondary}`} data-id="element-9505" data-mp="18jl4lxzk3quv">
                      Vendors
                    </span>
                  </div>
                  <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-9506" data-mp="209z8r2hrpotc">
                    {nonprofit.breakdown.vendors}%
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-9507" data-mp="1mcar03izyxsi">
                  <div className="flex items-center" data-id="element-9508" data-mp="1r7zsicekuorg">
                    <div className="w-2 h-2 rounded-full bg-rose-500 mr-2" data-id="element-9509" data-mp="av1b1qz2unvy"></div>
                    <span className={`text-sm ${dk.textSecondary}`} data-id="element-9510" data-mp="axdzmmzcbwhz">
                      Platform
                    </span>
                  </div>
                  <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-9511" data-mp="89k0zctjpdl4">
                    {nonprofit.breakdown.platform}%
                  </span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700" data-id="element-9512" data-mp="hbjkqcvy2vrr">
                <div className="flex items-center justify-center text-sm text-indigo-500 font-medium" data-id="element-9513" data-mp="cbovedrtie8t">
                  <TrendingUpIcon className="h-4 w-4 mr-1.5" data-id="element-9514" data-mp="3kr8isw5t52fy" />
                  Top 5% of supported causes
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default NonprofitProfile;